<script setup async>
import {competitionFacts} from '~/utils/privateAccess';
import CompetitionQuestionsPrompt from "@/components/competitions/CompetitionQuestionsPrompt.vue";
import FormatExplainerDialog from '~/components/formats/FormatExplainerDialog.vue';
import {useHeadService} from '~/composables/useHeadService.js';
import {useLadderRung} from '~/composables/useLadderRung.js';
import OutstandingFeeBanner from "~/components/payments/OutstandingFeeBanner.vue";
import {useAuthService} from '~/composables/useAuthService.js';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import ActionMenuItem from "~/components/navigation/ActionMenuItem.vue";
import JoinRequestsCard from "~/components/join/JoinRequestsCard.vue";
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import ClubChip from "~/components/clubs/ClubChip.vue";
import LadderRules from "~/components/ladders/LadderRules.vue";
import LadderPlayByNote from "~/components/ladders/LadderPlayByNote.vue";
import LadderView from "~/components/ladders/LadderView.vue";
import LadderChallengeCell from "~/components/ladders/LadderChallengeCell.vue";
import TeamCard from "~/components/teams/TeamCard.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import InformationDialog from "~/components/InformationDialog.vue";
import JoinRequestPending from "~/components/join/JoinRequestPending.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";
import JoinSettingsCard from "~/components/join/JoinSettingsCard.vue";
import PendingPlacementsCard from "~/components/placement/PendingPlacementsCard.vue";
import AwaitingPlacementBanner from "~/components/placement/AwaitingPlacementBanner.vue";
import {endpoints} from "~/network/endpoints.js";
import {useHttpRequest} from "~/composables/useHttpRequest.js";
import {useValidation} from "~/composables/useValidation.js";
import {watch, onMounted, ref, computed, onUnmounted} from "vue";
import {createError} from "nuxt/app";
import _ from "lodash";
import {useMailer} from "~/composables/useMailer.js";
import {useMessaging} from "~/composables/useMessaging.js";
import HandicapCalculatorButton from '~/components/handicaps/HandicapCalculatorButton.vue';
import HandicapMatchBanner from '~/components/handicaps/HandicapMatchBanner.vue';
import {useHandicapSettingsFor} from '~/composables/useHandicapSettingsFor';
import {useHandicapApi} from '~/composables/useHandicapApi';
import CompetitionPdfDialog from '~/components/competitions/CompetitionPdfDialog.vue';
import {canExportCompetitionPdf, EXPORT_PDF_LABEL} from '~/composables/useCompetitionPdf';

definePageMeta({
  name: 'LadderDetail',
  path: '/ladders/:ladderId',
});


const route = useRoute();
const router = useRouter();
const clubId = route.params.ladderId;

const {item: ladder, getItem: getPage, itemPending, hasItemErrors, itemErrors} = useCrudApi("api/v2/ladders");

// Handicap calculator in the action row: the ladder's settings (null = handicaps off) + every team on it
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => ladder.value?.id ? {ladderId: ladder.value.id} : null),
    computed(() => ladder.value?.handicapsEnabled === true));
const handicapEntries = computed(() => (ladder.value?.teams ?? []).filter((t) => t && t !== 'placeholder')
    .map((t) => ({teamId: t.id, name: t.name, value: t.handicap ?? null})));
await getPage(route.params.ladderId);

if (hasItemErrors.value) {
  console.warn("Error loading ladder", itemErrors.value);
  throw createError({
    statusCode: 404, // or another relevant status code
    message: JSON.stringify(itemErrors.value)
  });
}

const {t} = useI18n()
const {setMeta} = useHeadService();
setMeta(
    ladder.value?.clubName + ' / ' + ladder.value?.name + ' on Tap Leagues',
    ladder?.value?.clubName + ' ' + ladder?.value?.name + ' on Tap Leagues. Find out more about ' + ladder?.value?.name + ' and view matches and results.'
);
const {isAuthenticated, user, logout, isAdminOfClub, isMemberOfClub} = useAuthService();
const {leaveLadder, leavePending} = useLeaveEntity();
// Explicit imports: these live in components/fields/, so auto-import would
// name them FieldsSimpleDateTimeSelect. BasePage.vue imports them the same way.
import SimpleDateTimeSelect from "~/components/fields/SimpleDateTimeSelect.vue";
import PlayingAreaSelect from "~/components/fields/PlayingAreaSelect.vue";

const {outputDateFromDb, formatRange, inputDateForDb} = useCrudConverters();

const clubAdmin = computed(() => isAdminOfClub(ladder.value.clubId));

// The ladder has been deleted (archived) - show the deleted notice instead of content.
const ladderArchived = computed(() => !!ladder.value?.archivedAt);
const ladderPrivate = computed(() => !!ladder.value?.privateToViewer);
usePrivatePage(ladderPrivate);

const showLeaveLadderDialog = ref(false);
const rulesDialog = ref(false);

const titleActions = computed(() => {
  const actions = [];

  // Top-right header keeps ONLY the admin actions. View fixtures + Leave ladder
  // now live in the action bar (Leave is tucked under its "More" menu).
  if (clubAdmin.value) {
    actions.push({
      title: 'Edit ladder',
      icon: 'mdi-pencil',
      route: {name: 'ClubLadderSettings', params: {clubId: ladder.value.clubId, ladderId: ladder.value.id}},
      color: 'info',
    });
    // The roster editor (add/remove players, set positions) — "Edit ladder"
    // only reaches Settings (audit B3).
    actions.push({
      title: 'Players & positions',
      icon: 'mdi-account-group',
      route: {name: 'ClubLadderPlayers', params: {clubId: ladder.value.clubId, ladderId: ladder.value.id}},
      color: 'info',
    });
    actions.push({
      title: 'Create challenge',
      icon: 'mdi-plus',
      click: () => addFixtureDialog.value = true,
      color: 'success',
    });
  }

  return actions;
});

const {httpRequest} = useHttpRequest();
// Export PDF: club admins and super admins only (owner decision, 7 Oct 2026).
const pdfOpen = ref(false);
const canExportPdf = computed(() => !!ladder.value && canExportCompetitionPdf(user.value, ladder.value.clubId));

const isAdmin = computed(() => {
  return ladder.value && isAdminOfClub(ladder.value.clubId);
});

const isInLadder = computed(() => {
  if (!ladder.value?.teams || !user.value?.playerId) return false;
  return ladder.value.teams.some(t => t.players?.some(p => p.id === user.value.playerId));
});
// Joined and waiting for an admin to place them (JOIN_SETTINGS_PLAN.md §6) — no team yet,
// but no Join button either.
const isAwaitingPlacement = computed(() => !!ladder.value?.currentUserPendingPlacement);
const {rungLabel} = useLadderRung();
// Who is already on the ladder, for the place dialog: their players are not offered as
// team-mates, and a pair left short of a player can be completed where it stands.
const placementExistingTeams = computed(() => (ladder.value?.teams ?? []).map(t => ({
  teamId: t.id,
  name: t.name,
  where: rungLabel(t.newPosition),
  players: (t.players ?? []).map(p => ({id: p.id, fullName: p.fullName, imageFileName: p.imageFileName})),
})));

// Filter join link through the same visibility rules as the other competition pages
const visibleJoinLink = computed(() => {
  const raw = ladder.value?.joinLink;
  if (!raw) return null;
  if (isAdmin.value) return raw;

  // Non-admins require: visible (0), active, and scope-allowed
  const scopeAllowed = raw.inviteScope !== 1 || isMemberOfClub(ladder.value.clubId);
  return raw.inviteEntityPageSetting !== 1 && raw.isActive && scopeAllowed
      ? raw : null;
});

// The user's own pending join request for this ladder (from the auth user object).
const {isPending: hasPendingRequest, cancelling: cancelPending, cancelRequest} =
    useJoinRequestStatus('ladder', computed(() => ladder.value?.id));

// CHAT FUNCTIONALITY
const { entityChatLoading, entityChatErrors, createOrOpenEntityChat } = useMessaging();

// Check if user can access chat (participant or admin)
const canAccessChat = computed(() => {
  if (!isAuthenticated.value) return false;
  return isInLadder.value || clubAdmin.value;
});

const handleChatClick = async () => {
  await createOrOpenEntityChat(
    { ladderId: ladder.value.id, conversationId: ladder.value.conversationId },
    () => getPage(route.params.ladderId)
  );
};

const handleLeaveLadder = async () => {
  try {
    await leaveLadder(ladder.value.id);
    showLeaveLadderDialog.value = false;
    // Refresh the ladder data
    await getPage(route.params.ladderId);
  } catch (error) {
    console.error('Failed to leave ladder:', error);
    // Keep dialog open on error so user can see the error message
  }
};

const challengeCustomFilters = computed(() => {
  let filters = {ladderIds: [ladder.value.id], isActive: true};
  return filters;
});

const fixtureCustomFilters = computed(() => {
  let filters = {ladderIds: [ladder.value.id], isApproved: false};
  if (!isAdmin.value) {
    filters.playerIds = [user.value.playerId];
  }
  return filters;
});

// LOGIC FOR ADDING FIXTURE
const team1 = ref(null);
const team2 = ref(null);

// The start the two sides would play off, shown in both challenge dialogs (member and admin) —
// the same card the calculator shows, resolved by the server for THIS ladder's settings.
const {getPairingStart} = useHandicapApi();
const memberChallengeStart = ref(null);
const adminChallengeStart = ref(null);
const pairingStartFor = async (target, challengingTeamId, challengedTeamId) => {
  target.value = null;
  if (!handicapSettings.value || !challengingTeamId || !challengedTeamId || challengingTeamId === challengedTeamId) return;
  try {
    const res = await getPairingStart({homeTeamId: challengingTeamId, awayTeamId: challengedTeamId, ladderId: ladder.value.id});
    target.value = res;
  } catch {
    target.value = null;
  }
};
watch([() => route.query.challengingTeamId, () => route.query.challengedTeamId, handicapSettings],
    ([challenging, challenged]) => pairingStartFor(memberChallengeStart, challenging, challenged), {immediate: true});
watch([team1, team2, handicapSettings], ([t1, t2]) => pairingStartFor(adminChallengeStart, t1, t2));
const addFixtureDialog = ref(false);
const addFixtureForm = ref({});

// The server rejects frozen teams and pairings that share a player — only offer legal picks.
const challengeableTeams1 = computed(() => (ladder.value?.teams ?? []).filter(t => !t.frozen));
const challengeableTeams2 = computed(() => {
  const t1 = (ladder.value?.teams ?? []).find(t => t.id === team1.value);
  const team1Players = t1?.players?.map(p => p.id) ?? [];
  return challengeableTeams1.value.filter(t =>
      t.id !== team1.value && !t.players?.some(p => team1Players.includes(p.id)));
});
watch(team1, () => {
  if (team2.value != null && !challengeableTeams2.value.some(t => t.id === team2.value)) {
    team2.value = null;
  }
});

const {notify, addListener, removeLocalListenersWithEvent} = useListenerService();

// Use useCrudApi for better error handling
const {
  item: challengeResult,
  createItem: sendChallengeAction,
  formErrors: addFixtureErrors,
  formPending: addFixtureLoading,
  hasFormErrors: hasAddFixtureErrors
} = useCrudApi('api/v2/ladderChallenges');

const sendChallenge = async () => {
  const data = {
    ladderId: ladder.value.id,
    challengingTeamId: team1.value,
    challengedTeamId: team2.value,
  };
  await sendChallengeAction(data);
  if (!hasAddFixtureErrors.value) {
    addFixtureDialog.value = false;
    await router.push({
      name: 'LadderChallengeDetail',
      params: {ladderId: ladder.value.id, ladderChallengeId: challengeResult.value.id}
    });
  }
};
const {myTeamFromListOfTeams} = useValidation();
const challengeRowActions = (item) => {
  const rows = [
    {
      title: 'View challenge',
      icon: 'mdi-eye',
      color: 'primary',
      click: () => {
        router.push({
          name : 'LadderChallengeDetail',
          params: {ladderId: ladder.value.id, ladderChallengeId: item.id},
        });
      }
    }
  ];
  return rows;
};

const {
  item: ladderChallenge,
  getItem: getLadderChallenge,
  itemPending: ladderChallengePending,
  itemErrors: ladderChallengeErrors,
  deleteItem: deleteLadderChallenge,
  createItem: createLadderChallenge,
  formPending: ladderChallengeFormPending,
  formErrors: ladderChallengeFormErrors,
  hasFormErrors: ladderChallengeHasFormErrors
} = useCrudApi("api/v2/ladderChallenges");

const {
  items: challenges,
  getItems: getChallenges,
  listPending: challengesPending
} = useCrudApi("api/v2/ladderChallenges");

const {
  items: fixtures,
  getItems: getFixtures,
  listPending: fixturesPending
} = useCrudApi("api/v2/fixtures");

// The dialog is driven by the URL, so a direct load would mount it open during hydration and
// Vuetify's overlay scroll strategy throws (no root element yet) — hold it until mounted.
const isMounted = ref(false);
onMounted(() => { isMounted.value = true; });
const challengeDialog = computed({
  get: () => isMounted.value && (!!(route.query.challengingTeamId && route.query.challengedTeamId) || !!route.query.cancelLadderChallengeId),
  set: (value) => {
    if (value) {

    } else {
      let currentQuery = route.query;
      delete currentQuery.cancelLadderChallengeId;
      delete currentQuery.challengingTeamId;
      delete currentQuery.challengedTeamId;
      router.push({query: currentQuery, force: true});
    }
  }
})

const challengeAction = computed(() => {
  return route.query.cancelLadderChallengeId ? 'delete' : 'create';
});
// Same default as the accept dialog: tomorrow, rounded to the next half hour.
watch(() => ladderChallenge.value.suggestDate, (on) => {
  if (!on || ladderChallenge.value.date || ladderChallenge.value.time) return;
  const luxon = useNuxtApp().$luxon;
  const now = luxon.now();
  ladderChallenge.value.date = now.plus({days: 1}).toFormat('yyyy-MM-dd');
  const rounded = now.set({minute: 0, second: 0, millisecond: 0})
      .plus({minutes: Math.ceil(now.minute / 30) * 30});
  ladderChallenge.value.time = rounded.toFormat('HH:mm');
});

// A ticked box with half a time is not a suggestion, and a time that has
// already gone is not one either: say so rather than silently sending nothing.
const suggestionReady = computed(() => !!ladderChallenge.value.suggestDate
    && !!ladderChallenge.value.date && !!ladderChallenge.value.time);
const suggestionProblem = computed(() => {
  if (!ladderChallenge.value.suggestDate) return null;
  if (!suggestionReady.value) return 'Choose a date and a time, or untick the box.';
  const luxon = useNuxtApp().$luxon;
  const when = luxon.fromFormat(
      `${ladderChallenge.value.date} ${ladderChallenge.value.time}`, 'yyyy-MM-dd HH:mm');
  return when.isValid && when < luxon.now() ? 'That time has already passed.' : null;
});

// The tick, time and court are not part of the challenge model, so nothing
// cleared them: cancel a challenge to Anna and the form for Ben opened with
// Anna's time and court still set. Start clean each time the dialog opens.
watch(challengeDialog, (isOpen) => {
  if (!isOpen) return;
  ladderChallenge.value.suggestDate = false;
  ladderChallenge.value.date = null;
  ladderChallenge.value.time = null;
  ladderChallenge.value.suggestedPlayingAreaId = null;
});

const onChallengeForm = async () => {
  if (challengeAction.value !== 'delete' && suggestionProblem.value) return;
  if (challengeAction.value === 'delete') {
    await deleteLadderChallenge(ladderChallenge.value.id);
    notify('ladderChallengesUpdated', {});
  } else {
    await createLadderChallenge({
      ladderId: ladder.value.id,
      challengingTeamId: route.query.challengingTeamId,
      challengedTeamId: route.query.challengedTeamId,
      requestMessage: ladderChallenge.value.requestMessage || null,
      // Stored on the challenge, not scheduled: the other side still has to
      // accept before there is a fixture.
      suggestedDate: suggestionReady.value
          ? inputDateForDb(ladderChallenge.value.date + 'T' + ladderChallenge.value.time,
              "yyyy-MM-dd'T'HH:mm")
          : null,
      suggestedPlayingAreaId: ladderChallenge.value.suggestDate
          ? (ladderChallenge.value.suggestedPlayingAreaId || null)
          : null,
    })
  }
  if (!ladderChallengeHasFormErrors.value) {
    challengeDialog.value = false;
    notify('ladderChallengesUpdated', {});
    if(challengeAction.value === 'create') {
      await router.push({
        name: 'LadderChallengeDetail',
        params: {ladderId: ladder.value.id, ladderChallengeId: ladderChallenge.value.id}
      });
    }
  }
};

const challengesRef = ref(null);

const scrollToChallenges = () => {
  if (challengesRef.value) {
    challengesRef.value.scrollIntoView({behavior: 'smooth'});
  }
};

// Tab is driven by the URL (?tab=) so it's deep-linkable and shareable
// (e.g. from the stats page, now that the standalone challenges page is gone).
const validTabs = ['standings', 'challenges', 'awaitingApproval'];
const tab = computed({
  get: () => validTabs.includes(route.query.tab) ? route.query.tab : 'standings',
  set: (newTab) => {
    if (route.query.tab === newTab) return;
    router.replace({query: {...route.query, tab: newTab}});
  },
});

const loadChallengesAndFixtures = async () => {
  await Promise.all([
    getChallenges(1,1000, challengeCustomFilters.value),
    getFixtures(1,1000,fixtureCustomFilters.value)
  ]);
};

onMounted(async () => {
  if (route.query.cancelLadderChallengeId) {
    getLadderChallenge(route.query.cancelLadderChallengeId);
  }
  if (isAuthenticated.value) {
    await loadChallengesAndFixtures();
  }
  addListener('ladderChallengesUpdated', async () => {
    await getPage(route.params.ladderId);
    if (isAuthenticated.value) {
      await loadChallengesAndFixtures();
    }
  });
  addListener('fixtureUpdated', async () => {
    await getPage(route.params.ladderId);
    if (isAuthenticated.value) {
      await loadChallengesAndFixtures();
    }
  });
  // Approving/rejecting a join request updates accessApplicationCount.
  addListener('requestsUpdated', async () => {
    await getPage(route.params.ladderId);
  });
});
onUnmounted(() => {
  removeLocalListenersWithEvent('ladderChallengesUpdated');
  removeLocalListenersWithEvent('fixtureUpdated');
  removeLocalListenersWithEvent('requestsUpdated');
});

const hasStarted = computed(() => {
  return ladder.value && ladder.value.startDate && new Date(ladder.value.startDate) < new Date();
});

const hasEnded = computed(() => {
  return ladder.value && ladder.value.endDate && new Date(ladder.value.endDate) < new Date();
});

// Date chip label — guard nulls (ladders can be open-ended: no endDate, and
// sometimes no startDate) so formatRange never gets an Invalid Date.
const ladderDateLabel = computed(() => {
  const start = ladder.value?.startDate;
  const end = ladder.value?.endDate;
  if (start && end) return formatRange(start, end);
  if (start) return (hasStarted.value ? 'Started ' : 'Starts ') + outputDateFromDb(start, 'd MMM');
  return null;
});

const {filtersToQuery} = useMailer();

const showAwaitingApprovalTab = computed(() => {
  return isAuthenticated.value && fixtures.value && fixtures.value.length > 0;
});

const myTeam = computed(() => {
  if (!isAuthenticated.value || !user.value || !user.value.playerId || !ladder.value || !ladder.value.teams) {
    return null;
  }
  return myTeamFromListOfTeams(user.value.playerId, ladder.value.teams);
});

const isMyTeamFrozen = computed(() => {
  return myTeam.value?.frozen === true;
});

// Unfreeze functionality
const showUnfreezeDialog = ref(false);
const unfreezeLoading = ref(false);
const unfreezeErrors = ref({});

const handleUnfreezeTeam = async () => {
  unfreezeLoading.value = true;
  unfreezeErrors.value = {};
  try {
    await httpRequest(endpoints.ladders.unfreeze(ladder.value.id), {});
    showUnfreezeDialog.value = false;
    // Refresh ladder data
    await getPage(route.params.ladderId);
    // Refresh challenges and fixtures if authenticated
    if (isAuthenticated.value) {
      await loadChallengesAndFixtures();
    }
  } catch (e) {
    unfreezeErrors.value = e;
  } finally {
    unfreezeLoading.value = false;
  }
};

</script>

<template>
  <base-page>
    <!-- Deleted (archived) ladder - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): identity only, then the notice where the content would be -->
    <v-container v-if="ladderPrivate" class="large">
      <frontend-header :ladder="ladder" class="mb-8"
          :club="ladder.club ?? (ladder.clubId ? {id: ladder.clubId, slug: ladder.clubId, name: ladder.clubName} : null)"
          :title="ladder.name"
          title-icon="mdi-ladder"
          title-icon-color="ladder"
      >
        <template #subTitle>
          <club-chip v-if="ladder.club" :club="ladder.club"></club-chip>
          <span v-else-if="ladder.clubName" class="tl-chip">{{ ladder.clubName }}</span>
        </template>
      </frontend-header>
      <div class="tl-action-strip tl-action-strip--solo my-5">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>
      </div>
      <private-notice reason="competition" entity="ladder" :name="ladder.name" :club-name="ladder.clubName" :join-slug="ladder.visibleInviteLink?.slug"
                      :facts="competitionFacts('ladder', ladder, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
    </v-container>
    <v-container v-else-if="ladderArchived" class="large">
      <archived-notice entity="ladder" :name="ladder.name"/>
    </v-container>
    <v-container v-else class="large">

      <outstanding-fee-banner v-if="isAuthenticated" :source-id="route.params.ladderId"/>

      <crud-form-dialog
          delete-title="Cancel challenge"
          create-title="Send challenge"
          v-model:dialog="challengeDialog"
          :save-action="onChallengeForm"
          :action="challengeAction"
          :item-pending="ladderChallengePending"
          :form-pending="ladderChallengeFormPending"
          :item-errors="ladderChallengeErrors"
          :form-errors="ladderChallengeFormErrors"
          v-model="ladderChallenge">


        <template v-if="challengeAction === 'delete'">
          <v-alert type="warning" class="mb-3">Are you sure you want to cancel this challenge?</v-alert>
        </template>
        <template v-else>
          <h2 class="mb-3">You are challenging:</h2>
          <div class="bg-light pa-3 rounded mb-3">
            <team-card :expandable="true" size="large" :id="route.query.challengedTeamId"></team-card>
          </div>
          <!-- what the match would start on, off both sides' current handicaps -->
          <handicap-match-banner v-if="memberChallengeStart" :start="memberChallengeStart" class="mb-3"/>
          <div>
            <div class="tl-label mb-2">Leave a message (optional)</div>
            <v-textarea
                class="mb-2"
                :error="ladderChallengeErrors.requestMessage != null"
                :error-messages="ladderChallengeErrors.requestMessage"
                v-model="ladderChallenge.requestMessage"/>
          </div>

          <!-- Proposing a time up front saves a round trip. It stays a
               proposal: the other side still accepts, and can counter. -->
          <v-checkbox
              v-model="ladderChallenge.suggestDate"
              label="Suggest a time?"
              color="primary"
              hide-details
              class="mb-1"/>
          <template v-if="ladderChallenge.suggestDate">
            <simple-date-time-select
                class="mt-2"
                v-model:date="ladderChallenge.date"
                v-model:time="ladderChallenge.time"
                date-label="Suggested date"
                :date-error="ladderChallengeErrors.suggestedDate != null"
                :date-error-messages="ladderChallengeErrors.suggestedDate"/>
            <playing-area-select
                v-model="ladderChallenge.suggestedPlayingAreaId"
                :club-id="ladder.clubId"
                :sport-id="ladder.sportId"
                :date="ladderChallenge.date"
                :time="ladderChallenge.time"/>
            <div v-if="suggestionProblem" class="lc-hint lc-hint--error">{{ suggestionProblem }}</div>
            <div v-else class="lc-hint">They can agree to your time or suggest another.</div>
          </template>
        </template>

      </crud-form-dialog>

      <crud-form-dialog
          create-title="Create challenge"
          :disabled="team1 == null || team2 == null || team1 === team2" :form-pending="addFixtureLoading"
          :form-errors="addFixtureErrors" :save-action="sendChallenge" action="create" v-model="addFixtureForm"
          v-model:dialog="addFixtureDialog">
        <div class="tl-label mb-2">Challenger</div>
        <v-select
            outlined
            :items="challengeableTeams1"
            item-value="id"
            item-title="name"
            persistent-hint
            autocomplete="false"
            v-model="team1"
            :persistent-placeholder="true"
        ></v-select>

        <div class="tl-label mb-2">Challenge receiver</div>
        <v-select
            outlined
            :items="challengeableTeams2"
            item-value="id"
            item-title="name"
            persistent-hint
            autocomplete="false"
            v-model="team2"
            :persistent-placeholder="true"
        ></v-select>
        <handicap-match-banner v-if="adminChallengeStart" :start="adminChallengeStart" class="mt-1 mb-3"/>
        <v-alert class="mb-4" type="info">
          This will create a challenge between the two competitors and allow you to enter a result
        </v-alert>
      </crud-form-dialog>

      <frontend-header
          class="mb-8"
          :club="ladder.club"
          :ladder="ladder"
          :title="ladder.name"
          title-icon="mdi-ladder"
          title-icon-color="ladder"
          :actions="titleActions"
      >
        <template #title>
          <h1 class="mb-1">{{ ladder.name }}</h1>
        </template>
        <template #subTitle>
          <club-chip :club="ladder.club"></club-chip>
        </template>
      </frontend-header>

      <v-alert v-if="!hasStarted && !hasEnded" type="info" class="mb-4">
        <strong>This ladder hasn't started yet.</strong> This ladder will start on
        {{ outputDateFromDb(ladder.startDate, 'dd/MMM/yyyy') }}.
      </v-alert>

      <v-alert v-if="hasEnded" type="error" class="mb-4">
        <strong>This ladder has ended.</strong> This ladder ended on {{
          outputDateFromDb(ladder.endDate, 'dd/MMM/yyyy')
        }}.
      </v-alert>

      <v-progress-linear :active="itemPending" indeterminate color="primary"></v-progress-linear>

      <!-- Action strip (shared .tl-action-strip primitive — same as league/tournament) -->
      <div class="tl-action-strip mb-6">
        <!-- Export PDF (club admins and super admins only) -->
        <button v-if="canExportPdf" class="as" @click="pdfOpen = true">
          <v-icon size="17" icon="mdi-file-pdf-box"/>{{ EXPORT_PDF_LABEL }}
        </button>

        <!-- Share -->
        <short-link-share-wrapper
                    :join-reference-type="1" :join-reference-id="ladder.id" :can-share-join="clubAdmin">
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>

        <!-- Fixtures -->
        <router-link class="as" :to="{ name: 'FixtureSearch', query: { clubIds: [ladder.clubId], ladderIds: [ladder.id] } }">
          <v-icon size="17" icon="mdi-scoreboard"/>Matches
        </router-link>

        <!-- Stats -->
        <router-link class="as" :to="{ name: 'LadderStats', params: { ladderId: ladder.id } }">
          <v-icon size="17" icon="mdi-chart-line"/>Stats
        </router-link>

        <!-- Chat -->
        <button v-if="canAccessChat" class="as" @click="handleChatClick" :disabled="entityChatLoading">
          <v-icon size="17" icon="mdi-forum"/>{{ ladder.conversationId ? 'Join Chat' : 'Start Chat' }}
        </button>

        <!-- Handicap calculator (handicaps on) -->
        <handicap-calculator-button action :admin="isAdmin" :settings="handicapSettings" :entries="handicapEntries"
                                    :scoring-system-id="ladder?.scoringSystemId ?? null"
                                    :noun="(ladder?.playersPerTeam ?? 1) > 1 ? 'team' : 'player'"/>

        <!-- Pending request (already applied) -->
        <join-request-pending
            v-if="!isInLadder && !isAwaitingPlacement && hasPendingRequest"
            compact
            :entity-name="ladder.name"
            :cancelling="cancelPending"
            @cancel="cancelRequest"
        />

        <!-- Join -->
        <router-link
            v-else-if="!isInLadder && !isAwaitingPlacement && visibleJoinLink"
            class="as as--join"
            :to="'/invite/' + visibleJoinLink.slug"
        >
          <v-icon size="17" icon="mdi-account-plus"/>Join
        </router-link>

        <!-- Date range chip -->
        <template v-if="ladderDateLabel">
          <div class="vsep"></div>
          <span class="tl-chip as-chip" :class="(hasStarted && !hasEnded) ? 'tl-chip--ok' : ''">
            <v-icon size="14" icon="mdi-calendar"/>{{ ladderDateLabel }}
          </span>
        </template>

        <div class="vsep"></div>

        <!-- Rules -->
        <button class="as" @click="rulesDialog = true">
          <v-icon size="17" icon="mdi-format-list-checks"/>Rules
        </button>

        <!-- Admin: Email members -->
        <template v-if="isAdmin">
          <div class="vsep"></div>
          <router-link
              class="as"
              :to="{ ...route, query: { ...route.query, mailerQuery: filtersToQuery({ clubIds: [ladder.clubId], ladderIds: [ladder.id] }) } }"
          >
            <v-icon size="17" icon="mdi-email"/>Email
          </router-link>
        </template>

        <!-- Unfreeze (frozen team) -->
        <button v-if="isMyTeamFrozen" class="as" @click="showUnfreezeDialog = true">
          <v-icon size="17" icon="mdi-snowflake-off"/>Unfreeze
        </button>

        <!-- More — tucked-away personal actions -->
        <v-menu v-if="isInLadder" location="bottom end">
          <template #activator="{ props }">
            <button class="as" v-bind="props"><v-icon size="17" icon="mdi-dots-horizontal"/>More</button>
          </template>
          <v-list density="compact">
            <v-list-item @click="showLeaveLadderDialog = true">
              <template #prepend><v-icon size="18" color="error">mdi-exit-to-app</v-icon></template>
              <v-list-item-title class="text-error">Leave ladder</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Information (admin-authored rich text) — only when content exists -->
        <format-explainer-dialog format="ladder" :admin="isAdmin" :payments="ladder.entryFeeMinor != null"/>
        <competition-pdf-dialog v-if="canExportPdf" v-model="pdfOpen" format="ladder" :competition-id="ladder.id" :name="ladder.name"/>
        <information-dialog :html="ladder.informationHtml" :title="`${ladder.name} information`"/>
      </div>

      <!-- Member: when they next need to have played by (inactivity rule) -->
      <ladder-play-by-note v-if="myTeam" :ladder="ladder" :team="myTeam"/>

      <!-- Member: joined, waiting for an admin to place them -->
      <awaiting-placement-banner
          v-if="isAwaitingPlacement"
          :placement="ladder.currentUserPendingPlacement"
          :entity-name="ladder.name"
          @changed="getPage(route.params.ladderId)"
      />

      <!-- Admin: joining (link + quick settings) and the waiting area -->
      <join-settings-card
          v-if="clubAdmin"
          class="mb-4"
          :reference-type="1"
          :reference-id="ladder.id"
          :club-id="ladder.clubId"
          :entity-name="ladder.name"
          @changed="getPage(route.params.ladderId)"
      />
      <pending-placements-card
          v-if="clubAdmin"
          :reference-type="1"
          :reference-id="ladder.id"
          :club-id="ladder.clubId"
          :players-per-team="ladder.playersPerTeam"
          :count="ladder.pendingPlacementCount ?? 0"
          :ladder-entries="ladder.teams ?? []"
          :existing-teams="placementExistingTeams"
          :allow-multiple-teams="ladder.allowMultipleTeamsPerPlayer === true"
          :handicaps-enabled="ladder.handicapsEnabled === true"
          @changed="getPage(route.params.ladderId)"
      />

      <!-- Pending join requests (admins only; count rides on the ladder response) -->
      <join-requests-card
          v-if="clubAdmin"
          :count="ladder.accessApplicationCount ?? 0"
          :filters="{ ladderIds: [ladder.id] }"
      />

      <!-- Rules dialog (moved out of the toolbar menu) -->
      <tl-dialog v-model="rulesDialog" title="Ladder rules" :max-width="560" flush>
        <ladder-rules
            :show-squash-levels="ladder.sport.squashLevels"
            :unrestricted-challenges="ladder.numberUnrestrictedChallengesForCurrentUser"
            :auto-approve="ladder.autoApprove"
            :scoring-system-name="ladder.scoringSystemName"
            :ladder="ladder"></ladder-rules>
      </tl-dialog>

      <!-- scrolls inside itself if it ever outgrows a phone, never the page -->
      <!-- One tab is no tab: signed out there are no challenges to switch to. -->
      <div v-if="isAuthenticated" class="tl-seg ladder-seg mb-4">
        <button
            class="tl-seg__opt"
            :class="{ 'tl-seg__opt--active': tab === 'standings' }"
            @click="tab = 'standings'"
        >Standings</button>
        <button
            v-if="isAuthenticated"
            class="tl-seg__opt"
            :class="{ 'tl-seg__opt--active': tab === 'challenges' }"
            @click="tab = 'challenges'"
        >
          Challenges
          <v-chip v-if="challenges && challenges.length > 0" class="ml-1" size="x-small" variant="flat" color="primary">
            {{ challenges.length }}
          </v-chip>
        </button>
        <button
            v-if="showAwaitingApprovalTab"
            class="tl-seg__opt"
            :class="{ 'tl-seg__opt--active': tab === 'awaitingApproval' }"
            @click="tab = 'awaitingApproval'"
        >
          <span class="d-none d-sm-inline">Awaiting approval</span><span class="d-sm-none">Approve</span>
          <v-chip v-if="fixtures && fixtures.length > 0" class="ml-1" size="x-small" color="error">
            {{ fixtures.length }}
          </v-chip>
        </button>
      </div>

      <v-window v-model="tab">
        <v-window-item value="standings">
          <div class="tl-card">
            <!-- Nobody on it yet: say so, and give an admin the way forward
                 instead of a table with only a header row. -->
            <competitions-empty-state
                v-if="!(ladder.teams ?? []).length"
                entity-type-name="ladder"
                icon="mdi-ladder"
                :is-admin="clubAdmin"
                :ended="hasEnded">
              <v-btn color="primary" variant="flat" rounded="pill" size="small"
                     :to="{name: 'ClubLadderPlayers', params: {clubId: ladder.clubId, ladderId: ladder.id}}">
                Add players
              </v-btn>
            </competitions-empty-state>
            <ladder-view v-else :ladder="ladder" @open-challenges="tab = 'challenges'"></ladder-view>
          </div>
        </v-window-item>

        <v-window-item value="challenges" v-if="isAuthenticated">
          <div class="tl-card" ref="challengesRef">
            <v-progress-linear :active="challengesPending" indeterminate color="primary"></v-progress-linear>
            <template v-if="!challengesPending && challenges && challenges.length === 0">
              <div class="pa-6 text-center" style="color: rgb(var(--v-theme-textSubtle));">
                You don't have any outstanding challenges in this ladder
              </div>
            </template>
            <v-list v-else two-line class="pa-0">
              <template :key="challenge.id" v-for="(challenge,index) in challenges">
                <ladder-challenge-cell
                    :challenge="challenge"
                    :actions="challengeRowActions(challenge)"
                    :my-team="myTeam"
                />
                <v-divider
                    v-if="index < challenges.length - 1"
                    :key="index"
                ></v-divider>
              </template>
            </v-list>
          </div>
        </v-window-item>

        <v-window-item value="awaitingApproval" v-if="showAwaitingApprovalTab">
          <div class="rounded overflow-hidden">
            <v-progress-linear :active="fixturesPending" indeterminate color="primary"></v-progress-linear>
            <template v-if="!fixturesPending && fixtures && fixtures.length === 0">
              <div class="pa-6 text-center" style="color: rgb(var(--v-theme-textSubtle));">
                {{ t('approve_fixture_none') }}
              </div>
            </template>
            <template v-else>
              <fixture-cell class="mb-4" v-for="fixture in fixtures" :key="fixture.id"
                            :fixture="fixture"/>
            </template>
          </div>
        </v-window-item>
      </v-window>
    </v-container>

    <!-- Leave Ladder Confirmation Dialog -->
    <tl-dialog v-model="showLeaveLadderDialog" :title="`Leave ${ladder.name}?`" :max-width="400">
      <p class="mb-0">You'll be removed from the ladder and will need to request access again to rejoin.</p>
      <template #actions>
        <v-btn variant="outlined" @click="showLeaveLadderDialog = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" :loading="leavePending" @click="handleLeaveLadder">
          Leave ladder
        </v-btn>
      </template>
    </tl-dialog>

    <!-- Unfreeze Team Confirmation Dialog -->
    <tl-dialog v-model="showUnfreezeDialog"
               :title="(ladder?.playersPerTeam ?? 1) <= 1 ? 'Unfreeze yourself?' : 'Unfreeze your team?'"
               :max-width="400">
      <p class="mb-0">You'll be able to send and receive challenges again. Only an admin can freeze you again.</p>
      <template #actions>
        <v-btn variant="outlined" @click="showUnfreezeDialog = false">Cancel</v-btn>
        <v-btn color="primary" variant="flat" :loading="unfreezeLoading" @click="handleUnfreezeTeam">
          {{ (ladder?.playersPerTeam ?? 1) <= 1 ? 'Unfreeze' : 'Unfreeze team' }}
        </v-btn>
      </template>
    </tl-dialog>
    <competition-questions-prompt competition-type="ladder" :competition-id="ladder?.id"/>
  </base-page>
</template>

<style scoped lang="scss">
/* Standings / Challenges / Approve: fits a 360px phone; if it ever doesn't,
   it scrolls inside itself instead of pushing the page sideways. */
.ladder-seg {
  overflow-x: auto;
  scrollbar-width: none;

  .tl-seg__opt {
    flex-shrink: 0;
    white-space: nowrap;
  }

  /* phones: tighter tabs so all three fit at 360px */
  @media (max-width: 599.98px) {
    .tl-seg__opt {
      padding: 8px 10px;
      gap: 4px;
    }
  }
}

/* muted helper line under the suggested-time picker */
.lc-hint {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.45;
  color: rgb(var(--v-theme-textSubtle));
}

.lc-hint--error {
  color: rgb(var(--v-theme-error));
}
</style>
